import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  PhoneForwarded,
  RotateCw,
  WifiOff,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "@/components/atoms/Button";
import { whatsappNumberReplacementService } from "@/services/whatsappNumberReplacementService";
import { WhatsappNumberReplacementModal } from "./WhatsappNumberReplacementModal";
import { replacementErrorMessage } from "./replacementErrors";
import { describeFailure, type ReplacementPresentation } from "./replacementStatusModel";
import { useWhatsappReplacementStatus } from "./useWhatsappReplacementStatus";

/**
 * The "Change WhatsApp number" affordance and the live replacement state for a
 * single configured WhatsApp channel.
 *
 * Drops into the existing WhatsApp Configuration block on the Communication
 * Channels page. The credential fields there stay read-only and locked; this
 * is the one deliberate way to change the number, and everything it shows
 * comes from Atlas's status endpoint rather than from anything remembered in
 * the browser.
 *
 * ## Recovery actions
 *
 * Two states offer an operator action, and both invoke a real backend
 * operation rather than editing what the UI displays:
 *
 * - `CHATWOOT_DISPLAY_PENDING` → re-issues the cosmetic Chatwoot update.
 * - `ROLLBACK_FAILED` → re-runs the outstanding rollback compensations.
 *
 * There is deliberately **no** "Reset", "Force complete", "Mark rolled back" or
 * "Clear failure" affordance, and none may be added. `ROLLBACK_FAILED` can
 * describe a partly compensated distributed operation; a UI that let an
 * operator declare it finished would make Atlas claim the old number is
 * restored while downstream routing still disagreed. The only way out is for
 * the compensations to actually succeed, which is what Retry rollback attempts
 * and what the backend independently verifies before reporting `ROLLED_BACK`.
 */

export interface WhatsappNumberReplacementSectionProps {
  /** `communication_channels.id`. Absent until the channel has been created. */
  channelId?: string | null;
  entityName: string;
  currentPhone?: string | null;
  currentPhoneNumberId?: string | null;
  currentWabaId?: string | null;
  /**
   * Called when Atlas reports COMPLETED or ROLLED_BACK, i.e. the channel's own
   * number may have changed. The parent re-fetches the channel so the number
   * on screen comes from the backend and never from this component.
   */
  onChannelDataStale: () => void;
  /** Test seam only — polling interval for the status endpoint. */
  pollIntervalMs?: number;
}

const TONE_STYLES: Record<ReplacementPresentation["tone"], string> = {
  progress: "border-blue-200 bg-blue-50 text-blue-900",
  success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  warning: "border-amber-200 bg-amber-50 text-amber-900",
  danger: "border-red-200 bg-red-50 text-red-900",
};

const TONE_BAR: Record<ReplacementPresentation["tone"], string> = {
  progress: "bg-blue-500",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  danger: "bg-red-500",
};

/**
 * Icon per tone. Purely supplementary — the label and description already
 * carry the full meaning, so nothing here depends on colour alone.
 */
function ToneIcon({ presentation }: { presentation: ReplacementPresentation }) {
  const className = "w-5 h-5 shrink-0 mt-0.5";
  if (presentation.done) return <CheckCircle2 className={className} aria-hidden="true" />;
  if (presentation.inProgress) return <Loader2 className={`${className} animate-spin`} aria-hidden="true" />;
  return <AlertTriangle className={className} aria-hidden="true" />;
}

export const WhatsappNumberReplacementSection: React.FC<WhatsappNumberReplacementSectionProps> = ({
  channelId,
  entityName,
  currentPhone,
  currentPhoneNumberId,
  currentWabaId,
  onChannelDataStale,
  pollIntervalMs,
}) => {
  const { operation, presentation, initialised, connectionError, refresh, applyServerResponse } =
    useWhatsappReplacementStatus(channelId, {
      intervalMs: pollIntervalMs,
    });

  const [modalOpen, setModalOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const retryingRef = useRef(false);
  const [retryingRollback, setRetryingRollback] = useState(false);
  const retryingRollbackRef = useRef(false);
  /** Two-step confirmation for the rollback retry — it touches live routing. */
  const [confirmingRollbackRetry, setConfirmingRollbackRetry] = useState(false);
  const [dismissedId, setDismissedId] = useState<string | null>(null);

  /**
   * Replacements this session is actually involved in: ones it started, and
   * ones it saw while they still held the channel.
   *
   * Everything session-scoped keys off this. A replacement that completed last
   * month is history — announcing it on every page load, or forcing a channel
   * re-read because of it, would both be wrong. It is a render-visibility
   * cache, never a source of truth: the channel's state still comes only from
   * Atlas.
   */
  const sessionRef = useRef<Set<string>>(new Set());
  const [sessionTick, setSessionTick] = useState(0);
  /** Guards the one-shot channel refetch per resolved replacement. */
  const refetchedRef = useRef<Set<string>>(new Set());

  const replacementId = operation?.replacementId ?? null;
  const status = typeof operation?.status === "string" ? operation.status : null;
  const statusLocks = presentation?.locksChannel ?? false;

  const trackSession = useCallback((id: string | null | undefined) => {
    if (!id || sessionRef.current.has(id)) return;
    sessionRef.current.add(id);
    setSessionTick((t) => t + 1);
  }, []);

  // A replacement this session watched leave a non-terminal state means the
  // channel's own record may now hold a different number. Ask the parent to
  // re-read it — once per resolution, and never for a historical record that
  // was already terminal when the page loaded.
  useEffect(() => {
    if (!replacementId || !status) return;
    if (statusLocks) {
      trackSession(replacementId);
      return;
    }
    if (status !== "COMPLETED" && status !== "ROLLED_BACK") return;
    if (!sessionRef.current.has(replacementId)) return;
    const key = `${replacementId}:${status}`;
    if (refetchedRef.current.has(key)) return;
    refetchedRef.current.add(key);
    onChannelDataStale();
    // onChannelDataStale is a parent callback; including it would re-run this
    // whenever the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replacementId, status, statusLocks, trackSession]);

  if (!channelId) return null;

  const locked = statusLocks;
  // `sessionTick` is read so the ref mutation above forces this re-render.
  void sessionTick;
  const observed = !!replacementId && sessionRef.current.has(replacementId);
  const dismissed = !!replacementId && dismissedId === replacementId;
  // Show the panel for anything currently holding the channel, plus anything
  // that resolved while the operator was watching.
  const showPanel = !!presentation && (locked || (observed && !dismissed));

  const handleRetryChatwoot = async () => {
    // Ref, not the `retrying` state: two clicks dispatched before React
    // re-renders would both read the stale `false`.
    if (retryingRef.current) return;
    retryingRef.current = true;
    setRetrying(true);
    try {
      const op = await whatsappNumberReplacementService.retryChatwootDisplay(channelId);
      applyServerResponse(op);
      if (op.status === "COMPLETED") {
        toast.success("Chatwoot has been updated. The number change is complete.");
      } else {
        toast.info("Chatwoot has not confirmed the update yet. Messaging is unaffected — you can retry again.");
      }
    } catch (err) {
      toast.error(
        replacementErrorMessage(err, "The Chatwoot update could not be retried. Please try again shortly."),
      );
      // Whatever happened, Atlas is authoritative — re-read rather than guess.
      void refresh();
    } finally {
      retryingRef.current = false;
      setRetrying(false);
    }
  };

  /**
   * Resumes the backend's compensating rollback. Deliberately NOT a "reset" or
   * "mark as rolled back": it invokes the real retry endpoint, and whatever
   * Atlas answers — including "still ROLLBACK_FAILED" — is what gets rendered.
   * This function never synthesises ROLLED_BACK.
   */
  const handleRetryRollback = async () => {
    // Ref, not the `retryingRollback` state: two clicks dispatched before React
    // re-renders would both read the stale `false` and fire two POSTs. The
    // backend's compare-and-set would reject the second, but the UI must not
    // rely on that — this is the same guard the Chatwoot retry uses.
    if (retryingRollbackRef.current) return;
    retryingRollbackRef.current = true;
    setRetryingRollback(true);
    setConfirmingRollbackRetry(false);
    try {
      const op = await whatsappNumberReplacementService.retryRollback(channelId);
      // The response is the authority — including when it says the retry did
      // not succeed. Polling restarts by itself if the state warrants it
      // (ROLLBACK_IN_PROGRESS polls; ROLLBACK_FAILED and ROLLED_BACK do not).
      applyServerResponse(op);
      if (op.status === "ROLLED_BACK") {
        toast.success("Rollback completed. The previous WhatsApp number has been restored.");
      } else if (op.status === "ROLLBACK_IN_PROGRESS") {
        toast.info("Rollback is running. This page will keep checking until it finishes.");
      } else {
        toast.error(
          "The rollback did not complete. The channel is still locked — check the reason shown and retry once it is resolved.",
        );
      }
    } catch (err) {
      // A transport failure says nothing about what the backend did: the retry
      // may well have succeeded. Never render a failure from this — re-read.
      toast.error(
        replacementErrorMessage(err, "The rollback could not be retried. Re-checking the current state…"),
      );
      void refresh();
    } finally {
      retryingRollbackRef.current = false;
      setRetryingRollback(false);
    }
  };

  const failureText = presentation && !presentation.done
    ? describeFailure(operation?.failureCode, operation?.failureMessage)
    : null;

  const startDisabledReason = !initialised
    ? "Checking whether a number change is already running…"
    : locked
      ? "A number change is already in progress on this channel. It must finish before another can start."
      : !currentPhone?.trim() || !currentPhoneNumberId?.trim() || !currentWabaId?.trim()
        ? "Finish WhatsApp onboarding for this channel before replacing its number."
        : null;

  return (
    <div className="space-y-3">
      {showPanel && presentation && (
        <div
          role="status"
          aria-live="polite"
          className={`rounded-xl border p-4 space-y-3 ${TONE_STYLES[presentation.tone]}`}
        >
          <div className="flex gap-3">
            <ToneIcon presentation={presentation} />
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-bold">{presentation.label}</p>
                {!locked && observed && (
                  <button
                    type="button"
                    onClick={() => setDismissedId(replacementId)}
                    aria-label="Dismiss replacement status"
                    className="p-1 opacity-60 hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-xs leading-relaxed">{presentation.description}</p>

              {operation?.oldPhone && operation?.newPhone && (
                <p className="text-[11px] font-mono opacity-80">
                  {operation.oldPhone} → {operation.newPhone}
                </p>
              )}

              {failureText && <p className="text-xs leading-relaxed font-medium">{failureText}</p>}

              {presentation.needsOperator && operation?.replacementId && (
                <p className="text-[10px] font-mono opacity-70">Replacement ID {operation.replacementId}</p>
              )}
            </div>
          </div>

          {!presentation.done && (
            <div
              className="h-1.5 w-full rounded-full bg-white/60 overflow-hidden"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={presentation.progress}
              aria-label={`Replacement progress: ${presentation.label}`}
            >
              <div
                className={`h-full rounded-full transition-all duration-500 ${TONE_BAR[presentation.tone]}`}
                style={{ width: `${presentation.progress}%` }}
              />
            </div>
          )}

          {presentation.status === "CHATWOOT_DISPLAY_PENDING" && (
            <>
              <Button
                size="sm"
                variant="secondary"
                className="rounded-lg"
                loading={retrying}
                disabled={retrying}
                onClick={handleRetryChatwoot}
              >
                <RotateCw className="w-4 h-4" aria-hidden="true" />
                Retry Chatwoot update
              </Button>
            </>
          )}

          {presentation.status === "ROLLBACK_FAILED" && (
            <>
              {confirmingRollbackRetry ? (
                <div className="rounded-lg border border-red-300 bg-white/70 p-3 space-y-3">
                  <p className="text-xs leading-relaxed">
                    This will retry the remaining rollback operations and attempt to restore the previous
                    WhatsApp number configuration. Steps that already completed will not be repeated. It may
                    not succeed — if the underlying problem is still present, the channel will stay in this
                    state and you can retry again.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="rounded-lg"
                      loading={retryingRollback}
                      disabled={retryingRollback}
                      onClick={handleRetryRollback}
                    >
                      <RotateCw className="w-4 h-4" aria-hidden="true" />
                      Retry rollback now
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="rounded-lg"
                      disabled={retryingRollback}
                      onClick={() => setConfirmingRollbackRetry(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  className="rounded-lg"
                  loading={retryingRollback}
                  disabled={retryingRollback}
                  onClick={() => setConfirmingRollbackRetry(true)}
                >
                  <RotateCw className="w-4 h-4" aria-hidden="true" />
                  Retry rollback
                </Button>
              )}
            </>
          )}

          {connectionError && (
            <p className="flex items-center gap-1.5 text-[11px] font-medium opacity-80">
              <WifiOff className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              Can&apos;t reach BridgeAI right now — this is the last known state and will refresh automatically.
            </p>
          )}
        </div>
      )}

      <>
        <Button
          size="sm"
          variant="secondary"
          className="rounded-xl h-10 px-5"
          disabled={!!startDisabledReason}
          title={startDisabledReason ?? undefined}
          onClick={async () => {
            // Another operator may have started one since this page loaded —
            // the DB constraint would reject it anyway, but re-reading first
            // shows them why instead of failing in the dialog.
            const latest = await refresh();
            const latestStatus = typeof latest?.status === "string" ? latest.status : null;
            if (latestStatus && latestStatus !== "COMPLETED" && latestStatus !== "ROLLED_BACK"
              && latestStatus !== "VERIFICATION_FAILED") {
              return;
            }
            setModalOpen(true);
          }}
        >
          <PhoneForwarded className="w-4 h-4" aria-hidden="true" />
          Change WhatsApp number
        </Button>
      </>

      {startDisabledReason && locked && (
        <p className="text-[11px] text-bridge-muted">{startDisabledReason}</p>
      )}

      {modalOpen && (
        <WhatsappNumberReplacementModal
          channelId={channelId}
          entityName={entityName}
          currentPhone={currentPhone ?? ""}
          currentPhoneNumberId={currentPhoneNumberId ?? ""}
          currentWabaId={currentWabaId ?? ""}
          onClose={() => {
            setModalOpen(false);
            // A submit may have timed out on the way there and still created
            // the replacement. Closing without re-reading would leave the page
            // claiming there is nothing running.
            void refresh();
          }}
          onStarted={(op) => {
            setModalOpen(false);
            setDismissedId(null);
            // This session owns this replacement from here on, even if Atlas
            // ran the whole sequence inside the POST and answered COMPLETED.
            trackSession(op.replacementId);
            applyServerResponse(op);
          }}
          onAlreadyInProgress={() => {
            setModalOpen(false);
            toast.info("A number change is already running on this channel.");
            void refresh();
          }}
        />
      )}
    </div>
  );
};
